import { autofill, getFormAutofillValues } from '@mapbox/search-js-web'
import { Attrs, EventListeners, MinzeElement } from 'minze'
import { Font } from '../../../constants'
import { Callback } from '../../../modules/callback'
import { Locale } from '../../../modules/locale'

Locale.getInstance().registerKeySet('fr', 'lvc-mapbox-address-autofill', {
  addressLabel: 'Adresse*',
  addressLine2Label: 'Adresse 2',
  cityLabel: 'Ville*',
  provinceLabel: 'Province*',
  zipcodeLabel: 'Code postal*',
})

Locale.getInstance().registerKeySet('en', 'lvc-mapbox-address-autofill', {
  addressLabel: 'Address*',
  addressLine2Label: 'Address 2',
  cityLabel: 'City*',
  provinceLabel: 'Province*',
  zipcodeLabel: 'Postal code*',
})

export interface LvcMapboxAddressAutofill {
  placeAddress: string
  placeAddressLine2: string
  placeCity: string
  placeProvince: string
  placeZipcode: string
  onChange: string
}

// This component uses Mapbox address autofill and is used to create a "place."
export class LvcMapboxAddressAutofill extends MinzeElement {
  attrs: Attrs = [
    'place-address',
    'place-address-line2',
    'place-city',
    'place-province',
    'place-zipcode',
    ['on-change', ''],
  ]

  addressLine1Value = ''

  mapboxOptions = {}
  mapboxAccessToken =
    'pk.eyJ1IjoibGF2aXRyaW5lIiwiYSI6ImNtNXZpdmRpYzAyYncyanE5ZnpwY292cjkifQ.bvJtK_lbDiNVWG2kgUzeIQ'

  onReady(): void {
    this.mapboxOptions = {
      country: 'CA',
      language: Locale.language,
    }

    // Set up Mapbox autofill for the form inputs
    const formElement = this.shadowRoot?.querySelector('#mapbox-search-form')

    if (formElement) {
      autofill({
        accessToken: this.mapboxAccessToken,
        options: this.mapboxOptions,
      })
    } else {
      console.error('Form element not found.')
    }
  }

  // Function that gets called when the user selects a suggestion from the search box
  onRetrieve = (event: Event): void => {
    const customEvent = event as CustomEvent
    this.addressLine1Value = customEvent.detail.features[0].properties.address_line1 || ''
  }

  // Function that gets called when the user types in the form
  onFormChange = (event: Event): void => {
    event.preventDefault()

    const formElement = this.shadowRoot?.querySelector('#mapbox-search-form') as HTMLFormElement

    if (!formElement) {
      console.error('Form element not found.')
      return
    }
    const input = formElement.querySelector(
      'input[autocomplete~="street-address"]'
    ) as HTMLInputElement
    //  that function comes from mapbox itself and just gets all the values from the form
    const valueMap = getFormAutofillValues(formElement, input)

    // Call the callback with the collected data
    Callback.call(this.onChange, valueMap)
  }

  html = () => `
    <form id="mapbox-search-form">
      <mapbox-address-autofill
        access-token="${this.mapboxAccessToken}"
        options="${JSON.stringify(this.mapboxOptions)}"
      >
      <div class="input-container">
      <label for="address-line1">${Locale.getKey('lvc-mapbox-address-autofill.addressLabel')}</label>
        <input
          type="text"
          autocomplete="street-address"
          placeholder="${Locale.getKey('lvc-mapbox-address-autofill.addressLabel')}"
          class="input"
          value="${this.placeAddress || ''}"

        />
      </div>
      </mapbox-address-autofill>
      <div class="input-container">
        <label for="address-line2">${Locale.getKey('lvc-mapbox-address-autofill.addressLine2Label')}</label>
        <input value="${this.placeAddressLine2 || ''}" type="text" name="address-line2" autocomplete="address-line2" placeholder="${Locale.getKey('lvc-mapbox-address-autofill.addressLine2Label')}" class="input" />
      </div>
      <div class="input-container">
        <label for="city">${Locale.getKey('lvc-mapbox-address-autofill.cityLabel')}</label>
        <input value="${this.placeCity || ''}" type="text" name="city" autocomplete="address-level2" placeholder="${Locale.getKey('lvc-mapbox-address-autofill.cityLabel')}" class="input"/>
      </div>
      <div class="input-container">
        <label for="state">${Locale.getKey('lvc-mapbox-address-autofill.provinceLabel')}</label>
        <input value="${this.placeProvince || ''}"type="text" name="state" autocomplete="address-level1" placeholder="${Locale.getKey('lvc-mapbox-address-autofill.provinceLabel')}" class="input"/>
      </div>
      <div class="input-container">
        <label for="zip">${Locale.getKey('lvc-mapbox-address-autofill.zipcodeLabel')}</label>
        <input value="${this.placeZipcode || ''}" type="text" name="zip" autocomplete="postal-code" placeholder="${Locale.getKey('lvc-mapbox-address-autofill.zipcodeLabel')}" class="input"/>
      </div>
    </form>
  `

  css = () => `
    form {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
      max-width: 400px;
    }
    .input {
      background: transparent;
      border: 1px solid #1E007D;
      font-size: 16px;
      font-style: normal;
      font-family: inherit;
      font-weight: 500;
      line-height: 150%;
      border-radius: 999px;
      padding: 12px 16px;
      color: #1E007D;
      outline: none;
    }
    .input:focus, .input.has-value {
      border: 1px solid rgba(30, 0, 125, 0.3);
      background: #BFF0FF;
    }
    ::placeholder {
      color: rgba(30, 0, 125, 0.5);
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
    }
    :-ms-input-placeholder {
      color: rgba(30, 0, 125, 0.5);
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
    }
    ::-ms-input-placeholder {
      color: rgba(30, 0, 125, 0.5);
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
    }
    label {
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
      margin-bottom: 4px;
      font-family: ${Font.family};
      color:  #1E007D;
      line-height: 17px;
      margin-bottom: 4px;
    }
    .input-container {
      display: flex;
      flex-direction: column;
    }
  `

  eventListeners: EventListeners = [
    ['#mapbox-search-form', 'retrieve', this.onRetrieve],
    ['input', 'change', this.onFormChange],
  ]
}
